import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import {
  ackTenantMessagesForClaim,
  getTenantMessagesDashboard,
  TenantMessageGroup,
} from '../services/tenantMessagesApi';
import { brandColors } from '../styles/brandColors';
import DashboardClaimGroupCard, {
  DashboardClaimFacilityRow,
  DashboardNoteTimestamp,
} from './dashboard/DashboardClaimGroupCard';

const tileColors = {
  shell: '#f6f8fc',
  header: 'linear-gradient(135deg, #001545 0%, #0a3270 55%, #134074 100%)',
  headerIcon: 'rgba(255, 255, 255, 0.14)',
  border: 'rgba(0, 30, 96, 0.09)',
  messageText: '#1e293b',
};

const MESSAGE_PREVIEW_LINES = 2;
const MESSAGE_PREVIEW_MAX_CHARS = 120;

const truncateNote = (text: string): string => {
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (normalized.length <= MESSAGE_PREVIEW_MAX_CHARS) {
    return normalized;
  }
  return `${normalized.slice(0, MESSAGE_PREVIEW_MAX_CHARS).trimEnd()}…`;
};

const DashboardTenantMessages: React.FC = () => {
  const [groups, setGroups] = useState<TenantMessageGroup[]>([]);
  const [newCount, setNewCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [clearingClaimId, setClearingClaimId] = useState<number | null>(null);

  const loadMessages = useCallback(async () => {
    setError('');
    try {
      const data = await getTenantMessagesDashboard();
      setGroups(Array.isArray(data.groups) ? data.groups : []);
      setNewCount(data.new_count ?? 0);
    } catch {
      setError('Unable to load tenant messages.');
      setGroups([]);
      setNewCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const handleClearAll = async (claimId: number) => {
    setClearingClaimId(claimId);
    try {
      await ackTenantMessagesForClaim(claimId);
      setGroups((prev) => {
        const next = prev.filter((group) => group.claim_id !== claimId);
        const removed = prev.find((group) => group.claim_id === claimId);
        const removedCount = removed?.messages.length ?? 0;
        setNewCount((count) => Math.max(0, count - removedCount));
        return next;
      });
    } catch {
      setError('Unable to clear messages for this claim.');
    } finally {
      setClearingClaimId(null);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${tileColors.border}`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        width: '100%',
        height: '100%',
        minHeight: 260,
        backgroundColor: tileColors.shell,
        boxShadow: '0 10px 28px rgba(0, 21, 69, 0.1)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1.5,
          px: 2.5,
          py: 2,
          background: tileColors.header,
          borderBottom: `1px solid ${tileColors.border}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, minWidth: 0 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: tileColors.headerIcon,
              color: '#fff',
              flexShrink: 0,
            }}
          >
            <ForumOutlinedIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', lineHeight: 1.2, fontSize: '1.05rem' }}>
              Messages
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.78)', display: 'block', mt: 0.35 }}>
              New tenant replies awaiting review
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1.25,
            py: 0.5,
            borderRadius: 999,
            backgroundColor: newCount > 0 ? 'rgba(248, 113, 113, 0.28)' : 'rgba(255, 255, 255, 0.14)',
            color: '#fff',
            flexShrink: 0,
            border: newCount > 0 ? '1px solid rgba(255,255,255,0.2)' : '1px solid transparent',
          }}
        >
          <MailOutlineIcon sx={{ fontSize: 15 }} />
          <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: '0.75rem' }}>
            {newCount} New
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          backgroundColor: tileColors.shell,
          p: 1.5,
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Typography color="error" variant="body2" sx={{ p: 1.5 }}>
            {error}
          </Typography>
        ) : groups.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                mx: 'auto',
                mb: 1.25,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#e8edf5',
              }}
            >
              <MailOutlineIcon sx={{ fontSize: 24, color: 'rgba(0, 30, 96, 0.35)' }} />
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: brandColors.navy }}>
              No new tenant messages
            </Typography>
          </Box>
        ) : (
          groups.map((group) => (
            <DashboardClaimGroupCard
              key={group.claim_id}
              claimId={group.claim_id}
              claimNumber={group.claim_number}
              facilityId={group.facility_id}
              tenantName={group.tenant_name}
              statusLabel={group.status_label}
              timestamp=""
              hideMetaRow
              onClearAll={() => handleClearAll(group.claim_id)}
              clearing={clearingClaimId === group.claim_id}
              clearAllTooltip="Mark all messages for this claim as read"
              theme="messages"
            >
              <DashboardClaimFacilityRow
                facilityId={group.facility_id}
                tenantName={group.tenant_name}
                sx={{ mb: 0.75 }}
              />
              {group.messages.map((message, index) => (
                <Box
                  key={message.id}
                  sx={{
                    pt: index > 0 ? 1 : 0,
                    mt: index > 0 ? 1 : 0,
                    borderTop: index > 0 ? `1px solid ${tileColors.border}` : 'none',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography
                      variant="body2"
                      component="p"
                      sx={{
                        color: tileColors.messageText,
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                        lineHeight: 1.5,
                        m: 0,
                        flex: 1,
                        minWidth: 0,
                        display: '-webkit-box',
                        WebkitLineClamp: MESSAGE_PREVIEW_LINES,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordBreak: 'break-word',
                      }}
                    >
                      {truncateNote(message.note)}
                    </Typography>
                    <DashboardNoteTimestamp timestamp={message.created_at} />
                  </Box>
                </Box>
              ))}
            </DashboardClaimGroupCard>
          ))
        )}
      </Box>
    </Paper>
  );
};

export default DashboardTenantMessages;
